import _ from "lodash"
import pointInPolygon from "@turf/boolean-point-in-polygon"
import {
  getCenterFromGeometry,
  isValidLineString,
  getPointOnGeometry,
  isValidPoint,
} from "utils/geometry"
import { sendCaptureExceptionToSentry } from "../../../utils/sentry"
import {
  getExteriorBoundaryPolygon,
  isValidPolygon,
} from "components/IndoorMap"

// Used for clean up features removing missing feature from an Array of Features
const sanitizedFeatures = (features) => {
  return _.compact(features)
}

const bindAnchor = (feature, anchor) => {
  if (anchor) {
    feature.properties.anchor = anchor

    // TODO: Check if this is still used?
    feature.properties.ordinal = anchor?.properties?.unit?.properties?.ordinal
    feature.properties.level_name =
      anchor?.properties?.unit?.properties?.level?.properties?.name.en
    feature.properties.level_id =
      anchor?.properties?.unit?.properties?.level?.id
  }
}

const bindLevel = (feature, level) => {
  if (level) {
    feature.properties.level = level
    feature.properties.ordinal = level.properties.ordinal
    feature.properties.level_name = level?.properties?.name.en
  }
}

const findSectionKiosk = (feature, kiosks = []) => {
  const { geometry } = feature
  if (kiosks.length < 1) return
  const center = getPointOnGeometry(geometry)
  return kiosks.find((kiosk) => pointInPolygon(center, kiosk))
}

const findSectionUnit = (feature, units = []) => {
  const { geometry } = feature
  if (units.length < 1) return
  const center = getPointOnGeometry(geometry)
  return units.find((unit) => pointInPolygon(center, unit))
}

const bindLocalCategories = (feature, localCategories) => {
  feature.properties.local_categories = localCategories

  // Attach Parent Categories
  const parentCategories = localCategories.filter(
    (cat) => cat?.properties.parent_id === null
  )
  const subCategories = localCategories.filter(
    (cat) => cat?.properties.parent_id !== null
  )
  feature.properties.local_parent_categories = parentCategories
  feature.properties.local_sub_categories = subCategories
}

const bindGroups = (feature, groups) => {
  feature.properties.groups = groups
}

const bindUnit = (feature, unit) => {
  feature.properties.unit = unit
}

const bindKiosk = (feature, kiosk) => {
  feature.properties.kiosk = kiosk
}

const bindUnits = (feature, units) => {
  feature.properties.units = units

  // Default level at units[0]
  // TODO: Check if this is still used?
  const [defaultUnit] = units
  feature.properties.ordinal = defaultUnit.properties.ordinal
  feature.properties.level_name =
    defaultUnit.properties.level?.properties?.name.en
}

const bindOccupant = (feature, occupant) => {
  if (occupant) {
    feature.properties.occupant = occupant
  }
}

const bindFeature = (feature, childFeature) => {
  if (childFeature) {
    feature.properties.feature = childFeature
  }
}

const bindOrigin = (feature, origin) => {
  feature.properties.origin = origin
}

const bindDestination = (feature, destination) => {
  if (destination) {
    feature.properties.destination = destination
  }
}

export const WALKABLE_UNIT_CATEGORY = [
  "walkway",
  "parking",
  "room",
  "terrace",
  "unenclosedarea",
  "vegetation",
  "unspecified",
]
const getWalkableUnit = (units) => {
  const walkableUnits = units.filter((unit) =>
    WALKABLE_UNIT_CATEGORY.includes(`${unit?.properties?.category}`)
  )
  return walkableUnits[0]
}

const bindIntermediary = (feature, intermediary = null) => {
  feature.properties.intermediary = intermediary
}

const bindPromotions = (feature, promotions) => {
  feature.properties.promotions = promotions
}

const bindPrivileges = (feature, privileges) => {
  feature.properties.privileges = privileges
}

const bindSection = (feature, section) => {
  feature.properties.section = section
  feature.properties.section_id = section?.id
}

const bindVenue = (feature, venue) => {
  feature.properties.venue = venue
}

const IGNORE_UNIT_CATEGORIES = [
  "nonpublic",
  "opentobelow",
  "elevator",
  "escalator",
  "stairs",
  "unspecified",
]

const findFeatureInSection = (feature, sections = []) => {
  const { geometry, properties } = feature || {}
  const category = properties?.category

  if (sections.length < 1 || IGNORE_UNIT_CATEGORIES.includes(category)) return

  const validFeature = isValidPolygon(geometry?.coordinates)
    ? getExteriorBoundaryPolygon(feature)
    : feature
  const center = getPointOnGeometry(validFeature?.geometry)

  return sections.find((section) => pointInPolygon(center, section))
}

const findFeaturesInSection = (section, features = []) => {
  if (!section) return []

  return features.filter((feature) => {
    const { geometry, feature_type, properties } = feature || {}

    if (
      feature_type === "unit" &&
      IGNORE_UNIT_CATEGORIES.includes(`${properties?.category}`)
    ) {
      return false
    }

    const validFeature = isValidPolygon(geometry?.coordinates)
      ? getExteriorBoundaryPolygon(feature)
      : feature

    const center = getPointOnGeometry(validFeature?.geometry)
    if (!center) return false
    return pointInPolygon(center, section)
  })
}

const findAmenityKiosk = (feature, kiosks = []) => {
  try {
    const { geometry } = feature
    if (kiosks.length < 1) return
    const center = getCenterFromGeometry(geometry)
    return kiosks.find((kiosk) => pointInPolygon(center, kiosk))
  } catch (error) {
    console.warn("error finding amenity in kiosk", feature?.id)
  }
}

const getFeaturesObj = (features) => {
  const featureObj = {}
  for (let index = 0; index < features.length; index++) {
    const feature = features[index]
    try {
      featureObj[feature?.id] = feature
    } catch (error) {
      console.warn("error creating feature object", feature?.id)
    }
  }
  return featureObj
}

export const prepareMapDecorations = (mapDecorations) => {
  const returnArray = mapDecorations.reduce((acc, decoration) => {
    const decorations = decoration.geometries.map(
      (decorationGeometry, index) => {
        const {
          geometry,
          options,
          properties: decorationProperties,
        } = decorationGeometry
        const { symbol } = options
        const id = decoration.id + `-${index}`
        const properties = { symbol, ...decorationProperties }

        return { id, geometry, properties }
      }
    )
    return [...acc, ...decorations]
  }, [])

  return returnArray
}

export const prepareFeatures = (venueData) => {
  const {
    amenities = [],
    anchors = [],
    details = [],
    events = [],
    fixtures = [],
    kiosks = [],
    occupants = [],
    openings = [],
    privileges = [],
    promotions = [],
    relationships = [],
    sections = [],
    levels = [],
    units = [],
    features: featureCollection = [],
  } = venueData

  // Store feature in objects for easy find by id
  const featuresObj = getFeaturesObj(featureCollection)

  const findOne = (id) => {
    if (!id) return null
    try {
      if (!featuresObj[id])
        throw new Error(`[preparing] Cannot find feature with id ${id}`)
      return featuresObj[id]
    } catch (err) {
      console.warn(err.message)
      return null
    }
  }

  const prepareAmenity = (amenity) => {
    try {
      const { geometry } = amenity
      const { unit_ids, venue_id } = amenity.properties
      const amenityData = amenity

      if (!isValidPoint(geometry))
        throw new Error(`Invalid Point Coordinates for Amenity : ${amenity.id}`)

      if (unit_ids) {
        const units = sanitizedFeatures(unit_ids.map(findOne))
        bindUnits(amenityData, units)

        // Default level at units[0]
        // TODO: Check if this is still used?
        const [defaultUnit] = units
        //@ts-ignore
        amenityData.properties.ordinal = defaultUnit.properties.ordinal
        //@ts-ignore
        amenityData.properties.level_name =
          defaultUnit.properties.level?.properties?.name.en
      }

      // TODO : Add type to Unit
      const venue = findOne(venue_id)
      bindVenue(amenityData, venue)

      const kiosksInSameOrdinal =
        kiosksByOrdinal[amenityData.properties?.ordinal] || []

      const kiosk = findAmenityKiosk(amenity, kiosksInSameOrdinal)
      bindKiosk(amenityData, kiosk)
      // TODO: bindAddress(feature)
      // TODO: bindCorrelation(feature)
      featuresObj[amenityData.id] = amenityData
      return amenityData
    } catch (error) {
      console.warn(error.message)
      return null
    }
  }

  const prepareAnchor = (anchor) => {
    try {
      const { properties, geometry } = anchor
      const { unit_id } = properties
      const locatedUnit = findOne(unit_id)

      const anchorData = anchor
      // Validate Coordinates
      if (!isValidPoint(geometry))
        throw new Error(
          `Invalid Point Coordinates for Anchor : ${anchorData.id}`
        )

      // Validate Properties
      if (!locatedUnit)
        throw new Error(`Invalid Anchor without unit : ${anchorData.id}`)
      bindUnit(anchorData, locatedUnit)

      const level = locatedUnit?.properties?.level
      if (!level)
        throw new Error(`Invalid Anchor without level : ${anchorData.id}`)
      bindLevel(anchorData, level)

      const section = findFeatureInSection(
        anchorData,
        sectionsByOrdinal[anchorData.properties.ordinal]
      )
      bindSection(anchor, section)

      featuresObj[anchorData.id] = anchorData

      return anchorData
    } catch (error) {
      console.warn(error.message)
      return null
    }
  }

  const prepareOpening = (opening) => {
    try {
      const { geometry } = opening
      const { level_id, venue_id } = opening.properties
      const openingData = opening
      if (!isValidLineString(geometry))
        throw new Error(
          `Invalid LineString Coordinates for Opening : ${opening.id}`
        )
      const level = findOne(level_id)
      if (!level)
        throw new Error(`Can not find level for Opening : ${opening.id}`)
      bindLevel(openingData, level)

      const venue = findOne(venue_id)
      bindVenue(openingData, venue)

      featuresObj[openingData.id] = openingData

      return openingData
    } catch (error) {
      console.warn(error.message)
      return null
    }
  }

  const prepareRelationship = (relationship) => {
    const { origin, destination, intermediary, category } =
      relationship.properties
    try {
      const relationshipData = relationship

      if (origin) {
        bindOrigin(relationshipData, findOne(origin.id))
      }
      if (destination) {
        bindDestination(relationshipData, findOne(destination.id))
      }

      if (
        !relationshipData.properties.origin &&
        !relationshipData.properties.destination
      )
        throw new Error("relationship does not have origin and destination")

      bindIntermediary(
        relationshipData,
        sanitizedFeatures(intermediary?.map((feature) => findOne(feature.id)))
      )

      switch (category) {
        case "traversal": {
          const intermediaryData = relationshipData.properties.intermediary
          if (!_.isArray(intermediaryData) || _.isEmpty(intermediaryData))
            throw new Error("traversal relationship should have intermediary")
          break
        }
        case "stairs":
        case "stairs.emergencyexit":
        case "ramp":
        case "escalator":
        case "elevator": {
          if (
            !relationshipData.properties.origin ||
            !relationshipData.properties.destination
          )
            throw new Error(
              `${category} relationship does not have origin or destination`
            )
          break
        }
        default: {
          break
        }
      }

      featuresObj[relationship.id] = relationshipData
      return relationshipData
    } catch (error) {
      console.warn(error.message)
      return null
    }
  }

  // Prepare Levels
  levels.forEach((feature) => {
    const { venue_id } = feature.properties
    const venue = findOne(venue_id)
    bindVenue(feature, venue)
    featuresObj[feature.id] = feature
    // TODO: bindAddress(level)
    // TODO: bindBuildings(level)
  })

  // Prepare Sections
  sections.forEach((feature) => {
    const { level_id, venue_id, local_category_ids } = feature.properties
    const level = findOne(level_id)
    const venue = findOne(venue_id)

    bindLevel(feature, level)
    bindVenue(feature, venue)
    bindLocalCategories(
      feature,
      sanitizedFeatures(local_category_ids.map(findOne))
    )

    featuresObj[feature.id] = feature
    // TODO: bindAddress()
    // TODO: bindCorrelation()
    // TODO: bindParent()
  })

  const sectionsByOrdinal = _(sections)
    .groupBy((feature) => feature.properties.ordinal)
    .value()

  // Prepare Units
  units.forEach((feature) => {
    const { level_id, venue_id } = feature.properties
    const level = findOne(level_id)
    const venue = findOne(venue_id)
    bindLevel(feature, level)
    const section = findFeatureInSection(
      feature,
      sectionsByOrdinal[feature.properties.ordinal]
    )

    bindSection(feature, section)
    bindVenue(feature, venue)
    featuresObj[feature.id] = feature
    try {
      if (_.isNil(level_id) || _.isNil(_.get(feature, "properties.level")))
        throw new Error(
          `A level field has no defined on an unit feature (${feature?.id}).`
        )
    } catch (error) {
      sendCaptureExceptionToSentry({
        error,
        level: "warning",
        transactionName: `A level field has no defined.`,
      })
    }
  })

  // Prepare Anchors
  anchors.forEach(prepareAnchor)

  // Prepare Details
  details.forEach((feature) => {
    const { level_id } = feature.properties
    bindLevel(feature, findOne(level_id))
    featuresObj[feature.id] = feature
  })

  // Prepare Fixtures
  fixtures.forEach((feature) => {
    const { anchor_id, level_id, venue_id } = feature.properties
    const level = findOne(level_id)
    const venue = findOne(venue_id)
    bindAnchor(feature, findOne(anchor_id))
    bindLevel(feature, level)
    bindVenue(feature, venue)
    featuresObj[feature.id] = feature
  })

  // Prepare Kiosks
  kiosks.forEach((feature) => {
    const { anchor_id, level_id, venue_id } = feature.properties
    const level = findOne(level_id)
    const anchor = findOne(anchor_id)
    const venue = findOne(venue_id)
    bindAnchor(feature, anchor)
    bindLevel(feature, level)
    bindVenue(feature, venue)
    const section = findFeatureInSection(
      feature,
      sectionsByOrdinal[feature.properties.ordinal]
    )
    bindSection(feature, section)
    featuresObj[feature.id] = feature
    try {
      if (_.isNil(level_id) || _.isNil(_.get(feature, "properties.level")))
        throw new Error(
          `A level field has no defined on a kiosk feature (${feature?.id}).`
        )
    } catch (error) {
      sendCaptureExceptionToSentry({
        error,
        level: "warning",
        transactionName: `A level field has no defined.`,
      })
    }
  })

  const kiosksByOrdinal = _(kiosks)
    .groupBy((feature) => feature.properties.ordinal)
    .value()

  const unitsByOrdinal = _(units)
    .groupBy((feature) => feature.properties.ordinal)
    .value()

  // Prepare Amenities
  amenities.forEach(prepareAmenity)

  // Prepare Occupants
  occupants.forEach((feature) => {
    const {
      anchor_id,
      local_category_ids,
      kiosk_id,
      unit_id,
      promotion_ids,
      privilege_ids,
      group_ids = [],
      venue_id,
    } = feature.properties
    const anchor = findOne(anchor_id)

    bindAnchor(feature, anchor)
    bindLocalCategories(
      feature,
      sanitizedFeatures(local_category_ids.map(findOne))
    )
    bindGroups(feature, sanitizedFeatures(group_ids.map(findOne)))
    const kiosk = findOne(kiosk_id)
    bindKiosk(feature, kiosk)
    const unit = findOne(unit_id)
    bindUnit(feature, unit)
    bindPromotions(feature, sanitizedFeatures(promotion_ids.map(findOne)))
    bindPrivileges(feature, sanitizedFeatures(privilege_ids.map(findOne)))
    bindVenue(feature, findOne(venue_id))

    const section =
      _.get(kiosk, "properties.section", undefined) ||
      _.get(unit, "properties.section", undefined)

    bindSection(feature, section)

    featuresObj[feature.id] = feature
    // TODO: bindCorrelation(feature)
  })

  // Prepare Openings
  openings.forEach(prepareOpening)

  // Prepare Relationships
  relationships.forEach(prepareRelationship)

  /**
   * Non- imdf
   */

  // Prepare Events
  events.forEach((feature) => {
    const { feature_id, venue_id, local_category_ids = [] } = feature.properties
    const location = findOne(feature_id)
    const venue = findOne(venue_id)
    bindLocalCategories(
      feature,
      sanitizedFeatures(local_category_ids.map(findOne))
    )
    bindFeature(feature, location)
    bindVenue(feature, venue)
    try {
      if (!_.isNil(feature_id) && _.isNil(_.get(feature, "properties.feature")))
        throw new Error(
          `Unable to find a feature data (${feature_id}) to binding a event's feature id (${feature?.id}).`
        )
    } catch (error) {
      sendCaptureExceptionToSentry({
        error,
        level: "error",
        transactionName: `Unable to find a feature data.`,
      })
    }
  })

  // Prepare Promotion
  promotions.forEach((feature) => {
    const { feature_id, venue_id, local_category_ids = [] } = feature.properties
    const location = findOne(feature_id)
    const venue = findOne(venue_id)
    bindLocalCategories(
      feature,
      sanitizedFeatures(local_category_ids.map(findOne))
    )
    bindFeature(feature, location)
    bindVenue(feature, venue)
  })

  // Prepare Promotion
  privileges.forEach((feature) => {
    const { occupant_id, venue_id } = feature.properties
    const occupant = findOne(occupant_id)
    const venue = findOne(venue_id)
    bindOccupant(feature, occupant)
    bindVenue(feature, venue)
  })

  /**
   * Post-preparing data
   */
  const postProcessSection = (sectionData) => {
    try {
      const sectionOrdinal = sectionData.properties.ordinal
      const unitsOnLevel = unitsByOrdinal[sectionOrdinal] || []
      const kiosksOnLevel = kiosksByOrdinal[sectionOrdinal] || []
      const units = findFeaturesInSection(sectionData, unitsOnLevel)
      const kiosks = findFeaturesInSection(sectionData, kiosksOnLevel)

      sectionData.properties.units = units
      sectionData.properties.unit_ids = units.map((unit) => unit.id)
      sectionData.properties.kiosks = kiosks
      sectionData.properties.kiosk_ids = units.map((kiosk) => kiosk.id)

      // Find nearest unit with a section display point; otherwise, use walkableUnit
      const unit = findSectionUnit(sectionData, units) || getWalkableUnit(units)
      sectionData.properties.unit = unit
      sectionData.properties.unit_id = unit?.id

      const kiosk = findSectionKiosk(sectionData, kiosks) || kiosks[0]
      sectionData.properties.kiosk = kiosk
      sectionData.properties.kiosk_id = kiosk?.id

      return sectionData
    } catch (error) {
      return null
    }
  }
  sections.forEach(postProcessSection)

  venueData.featuresObj = featuresObj
  venueData.features = Object.values(featuresObj)
}
